// Sidebar catalog. The order of the `warehouse` group mirrors the module tour in
// `docs/design_handoff_warehouse_management/README.md`; the `system` group keeps
// the starter's own admin screens, which the handoff does not cover.
// The component catalog and demo `items` slice stay absent because they are
// developer tooling. Dashboard is the first operational entry and therefore
// also the landing screen after login.
//
// `unbuilt: true` marks a module the router still resolves to
// `ModulePlaceholderPage` because its backend slice has not landed. The route
// stays reachable and permission-gated; the sidebar just stops advertising it,
// since a menu entry that always opens the same "chưa xây dựng" card costs a
// click and teaches nothing. Drop the flag in the same change that gives the
// module a real page — the router's own `component` is what actually decides
// which screen renders.
const NAV_GROUPS = [
  { key: 'warehouse', label: 'Nghiệp vụ kho' },
  { key: 'system', label: 'Hệ thống' },
];
const NAV_ROUTES = [
  { name: 'dashboard', label: 'Dashboard', icon: 'dashboard', group: 'warehouse' },
  { name: 'materials', label: 'Danh mục vật tư', icon: 'materials', permission: 'catalog.view', group: 'warehouse' },
  { name: 'shared-catalogs', label: 'Danh mục dùng chung', icon: 'catalog', permission: 'catalog.view', group: 'warehouse' },
  { name: 'inventory', label: 'Tồn kho', icon: 'inventory', permission: 'inventory.view', group: 'warehouse' },
  { name: 'receipts', label: 'Phiếu nhập', icon: 'receipt', permission: 'documents.view', group: 'warehouse' },
  { name: 'issues', label: 'Phiếu xuất', icon: 'issue', permission: 'documents.view', group: 'warehouse' },
  { name: 'cutting', label: 'Lệnh cắt', icon: 'cutting', permission: 'cutting_orders.view', group: 'warehouse', unbuilt: true },
  { name: 'btp', label: 'Kho BTP', icon: 'btp', permission: 'stock_unit_splits.view', group: 'warehouse', unbuilt: true },
  { name: 'requests', label: 'Yêu cầu cấp BTP', icon: 'request', permission: 'btp_delivery.view', group: 'warehouse', unbuilt: true },
  { name: 'stocktake', label: 'Kiểm kê', icon: 'stocktake', permission: 'stocktakes.view', group: 'warehouse' },
  { name: 'defect-reports', label: 'Biên bản lỗi', icon: 'defect', permission: 'defect_reports.view', group: 'warehouse' },
  { name: 'reports', label: 'Báo cáo', icon: 'reports', permission: 'reports.view', group: 'warehouse', unbuilt: true },
  { name: 'users', label: 'Nhân viên', icon: 'users', permission: 'users.view', group: 'system' },
  { name: 'audit', label: 'Nhật ký', icon: 'activity', permission: 'audit.view', group: 'system' },
  // `/settings` tồn tại từ đầu nhưng **không có lối vào nào**: không mục sidebar,
  // không mục ở topbar, nên cách duy nhất tới được là gõ URL hoặc đi qua lối
  // chuyển hướng `/warehouses` mà revision `015` để lại. Mà nó lại là chỗ duy
  // nhất đổi được tên kho và xem được kho của xưởng — tức một màn hình có thật,
  // gác bằng quyền có thật, mà không ai bấm tới được. Glyph `settings` đã nằm
  // sẵn trong `NavIcon.vue` từ trước, chỉ thiếu đúng dòng này.
  { name: 'settings', label: 'Cài đặt', icon: 'settings', permission: 'settings.manage', group: 'system' },
];
// Groups the nav by section, dropping whatever the signed-in user cannot reach
// and whatever has no screen behind it yet, so an empty section never renders a
// bare heading.
function navGroups(permissions = []) {
  const visible = NAV_ROUTES.filter((item) => !item.unbuilt && (!item.permission || permissions.includes(item.permission)));
  return NAV_GROUPS.map((group) => ({ ...group, items: visible.filter((item) => item.group === group.key) })).filter((group) => group.items.length);
}
// Where `/` and a fresh login land. It is the first entry of the sidebar the
// signed-in user can actually reach. Dashboard has no domain permission of its
// own because each block and action checks its source permission independently.
// Landing remains derived from `navGroups` so sidebar order and login behavior
// cannot drift apart later.
function landingRoute(permissions = []) {
  return navGroups(permissions)[0]?.items[0]?.name || 'forbidden';
}
const CATALOG_CATEGORIES = ['forms', 'tables', 'modals', 'dates', 'uploads', 'feedback', 'display'];
const ITEM_STATUSES = [
  { label: 'Đang hoạt động', value: 'active' },
  { label: 'Ngừng hoạt động', value: 'inactive' },
  { label: 'Bản nháp', value: 'draft' },
];
function resolveApiBase(env = {}) { return env.VUE_APP_API_BASE_URL || env.VUE_APP_API_URL || '/api/v1'; }

function paginationWindow(total, current, radius = 1) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total]);
  for (let page = current - radius; page <= current + radius; page += 1) if (page > 1 && page < total) pages.add(page);
  const sorted = [...pages].sort((a, b) => a - b);
  const result = [];
  sorted.forEach((page, index) => {
    if (index && page - sorted[index - 1] > 1) result.push('…');
    result.push(page);
  });
  return result;
}
function serializeQuery(query = {}) {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === '' || value === null || value === undefined) return;
    (Array.isArray(value) ? value : [value]).forEach((item) => params.append(key, String(item)));
  });
  return params.toString();
}
function validateUpload(file, rules = {}) {
  const errors = [];
  const ext = String(file.name || '').split('.').pop().toLowerCase();
  // An empty `extensions` list means *do not check the extension*, not *reject
  // everything*: the biên bản lỗi attachment box accepts whatever tool made the
  // print file, and the server's `policy=opaque` is what actually decides.
  if (rules.extensions?.length && !rules.extensions.map((x) => x.toLowerCase()).includes(ext)) errors.push('Định dạng file không được hỗ trợ');
  if (rules.mimeTypes?.length && !rules.mimeTypes.includes(file.type)) errors.push('Loại nội dung file không hợp lệ');
  if (rules.maxSize && file.size > rules.maxSize) errors.push(`File vượt quá ${Math.ceil(rules.maxSize / 1048576)} MB`);
  return errors;
}
function normalizeFormErrors(error) {
  const fields = error?.response?.data?.error?.field_errors || error?.response?.data?.error?.fields || {};
  return Object.fromEntries(Object.entries(fields).map(([key, value]) => [key, Array.isArray(value) ? value[0] : value]));
}
function toDateOnly(value) { return value ? String(value).slice(0, 10) : null; }
function toIsoDateTime(value) { return value ? new Date(value).toISOString() : null; }
function localDateString(value = new Date(), timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone) { const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(value); const get = (type) => parts.find((part) => part.type === type).value; return `${get('year')}-${get('month')}-${get('day')}`; }
function canAccess(meta = {}, auth = {}) {
  if (meta.requiresAuth && !auth.authenticated) return false;
  if (!meta.permissions?.length) return true;
  return meta.permissions.some((permission) => auth.permissions?.includes(permission));
}

// flatpickr's range mode joins the two dates with its locale's
// `rangeSeparator`, and the app runs the Vietnamese locale (`main.js`), so that
// separator is " đến " — not the library default " to ". Both halves of the
// round trip live here because getting them from two places is exactly how
// `DateRangePicker` ended up emitting Vietnamese presets into a picker parsing
// English: the preset was written, the picker never re-read it.
const DATE_RANGE_SEPARATOR = ' đến ';
// `["2026-08-01", "2026-09-30"]` -> `"2026-08-01 đến 2026-09-30"`. A range with
// only a start (flatpickr emits one while the second date is still being
// picked) formats as that date alone, which is what the picker itself shows.
function formatDateRange(from, to) { return [from, to].filter(Boolean).join(DATE_RANGE_SEPARATOR); }
// The inverse, always a pair: `["", ""]` for empty, and `[from, ""]` for a
// half-picked range so a caller can send one bound without inventing the other.
function parseDateRange(value) {
  const [from = '', to = ''] = String(value || '').split(DATE_RANGE_SEPARATOR).map((part) => part.trim());
  return [from, to];
}

module.exports = { DATE_RANGE_SEPARATOR, formatDateRange, parseDateRange, NAV_ROUTES, NAV_GROUPS, navGroups, landingRoute, CATALOG_CATEGORIES, ITEM_STATUSES, resolveApiBase, paginationWindow, serializeQuery, validateUpload, normalizeFormErrors, toDateOnly, toIsoDateTime, localDateString, canAccess };
